const _ = require('lodash');
const { createPopper } = require('@popperjs/core');
const geojsonNormalize = require('@mapbox/geojson-normalize');

const CodeMirror = require('codemirror/lib/codemirror');
require('codemirror/addon/fold/foldcode');
require('codemirror/addon/fold/foldgutter');
require('codemirror/addon/fold/brace-fold');
require('codemirror/addon/edit/matchbrackets');
require('codemirror/mode/javascript/javascript');

const validate = require('../lib/validate');
const zoomextent = require('../lib/zoomextent');
const flash = require('../ui/flash');

const ShapeDetector = require('shape-detector');

module.exports = function (context) {
  CodeMirror.keyMap.tabSpace = {
    Tab: function (cm) {
      const spaces = new Array(cm.getOption('indentUnit') + 1).join(' ');
      cm.replaceSelection(spaces, 'end', '+input');
    },
    fallthrough: ['default']
  };

  function render(selection) {
    const textarea = selection.html('').append('textarea');

    // copy button tooltip
    const tooltip = selection
      .append('div')
      .attr('id', 'tooltip')
      .attr(
        'class',
        'opacity-0 text-white font-medium text-xs rounded text-left py-1 px-2 bg-mb-gray-dark transition-opacity duration-100'
      )
      .attr('role', 'tooltip')
      .text('Copied!');

    // tooltip arrow
    tooltip
      .append('div')
      .attr('id', 'arrow')
      .attr(
        'class',
        '-right-1 top-0 translate-y-2 absolute w-2 h-2 bg-transparent before:opacity-0 before:transition-opacity before:duration-100 group-hover:before:opacity-100 before:absolute before:w-2 before:h-2 before:rotate-45 before:bg-mb-gray-dark'
      )
      .attr('data-popper-arrow', '');

    // adds a copy button
    const buttonContainer = selection
      .append('div')
      .attr(
        'class',
        'mapboxgl-ctrl mapboxgl-ctrl-group absolute right-5 top-5 opacity-0 group-hover:opacity-100 transition-opacity duration-100'
      );

    const editor = CodeMirror.fromTextArea(textarea.node(), {
      mode: { name: 'javascript', json: true },
      matchBrackets: true,
      tabSize: 2,
      gutters: ['error', 'CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
      theme: 'eclipse',
      autofocus: window === window.top,
      keyMap: 'tabSpace',
      lineNumbers: true,
      foldGutter: true
    });

    const button = buttonContainer
      .append('button')
      .attr('id', 'copy-button')
      .attr('title', 'Copy');

    const buttonIcon = button
      .append('span')
      .attr('class', 'fa-solid fa-copy text-gray-500');

    button.on('click', () => {
      // copy to clipboard
      navigator.clipboard.writeText(editor.getValue());

      // set the button to a green checkmark
      buttonIcon
        .classed('fa-copy', false)
        .attr('class', 'fa-solid fa-check text-green-600');
      // show tooltip
      tooltip.classed('group-hover:opacity-100', true);
      setTimeout(() => {
        buttonIcon.attr('class', 'fa-solid fa-copy text-gray-500');
        // hide tooltip
        tooltip.classed('group-hover:opacity-100', false);
      }, 3000);
    });

    const copyButtonEl = document.querySelector('#copy-button');
    const tooltipEl = document.querySelector('#tooltip');

    createPopper(copyButtonEl, tooltipEl, {
      placement: 'left',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        }
      ]
    });

    // blur the editor so map keybindings will work on initial load
    editor.display.input.blur();

    editor.foldCode(CodeMirror.Pos(0, 0));
    editor.matchBrackets();

    editor.on('beforeChange', (cm, change) => {
      if (change.origin === 'paste') {
        try {
          const newText = JSON.stringify(JSON.parse(change.text[0]), null, 2);
          change.update(null, null, newText.split('\n'));
        } catch (e) {
          console.log('error pretty-printing pasted geojson', e);
        }
      }
    });

    editor.on('change', validate(changeValidated));

    function changeValidated(err, data, zoom) {
      if (!err) {
        let updateSource = 'json';
        const originalType = data.type;
        // normalize into a FeatureCollection
        if (
          [
            'Feature',
            'GeometryCollection',
            'Point',
            'LineString',
            'Polygon',
            'MultiPoint',
            'MultiLineString',
            'MultiPolygon'
          ].includes(data.type)
        ) {
          data = geojsonNormalize(data);
          updateSource = 'geojsonNormalize';

          flash(
            context.container,
            `The imported ${originalType} was normalized into a FeatureCollection`
          );
        }

        // don't set data unless it has actually changed
        if (!_.isEqual(data, context.data.get('map'))) {
          context.data.set({ map: data }, updateSource);
          if (zoom) zoomextent(context);
        }
      }
    }

    context.dispatch.on('change.json', (event) => {
      if (event.source !== 'json') {
        console.log(context.data.get('map')); // Debug
        let text = '';
        const scrollInfo = editor.getScrollInfo();
        text += getCoords(context.data.get('map')); // Get circles
        text += printDMSCoords(context.data.get('map')); // Get shapes
        editor.setValue(text);
        editor.scrollTo(scrollInfo.left, scrollInfo.top);
      }
    });

    let text = '';
    text += getCoords(context.data.get('map')); // Get circles
    text += printDMSCoords(context.data.get('map')); // Get shapes
    editor.setValue(text);

    // Return DMS Coords
    function getCoords(object) {
      let text = ''; // Return text
      let coords = []; // Temp coords

      // Access the features and coordinates
      const features = object.features;

      // Loop through each feature
      features.forEach((feature, featureIndex) => {
        // Check if the geometry type is valid
        if (feature.geometry.type !== 'Point') {
          const coordinates = feature.geometry.coordinates[0];

          // Loop through each coordinate
          coordinates.forEach((coord, index) => {
            coords.push({ lat: coord[1], lon: coord[0] });
          });

          // Attempt this on the shape, won't work if not a circle
          if (isCircleMath(coords, 'circle')) {
            try {
              text += analyzeCircle(coords, featureIndex);
            } catch (e) {
              console.log(e);
            }
          }

          coords = [];
        }
      });

      return text;
    }

    // Return DMS Coords
    function printDMSCoords(object) {
      let text = ''; // Output

      // Access the features and coordinates
      const features = object.features;

      // Loop through each feature
      features.forEach((feature, index) => {
        // Check if the geometry type is LineString
        if (feature.geometry.type === 'LineString') {
          // Seperate
          text += 'Line ' + (index + 1) + ':\n';

          const coordinates = feature.geometry.coordinates;

          // Loop through each coordinate
          coordinates.forEach((coord, index) => {
            text += convertToDMS(coord[1], coord[0]);
            text += ';\n';

            // Console Output:
            console.log('DMS: ' + convertToDMS(coord[1], coord[0]));
            console.log(
              `Coordinate ${index + 1}: Latitude = ${coord[1]}, Longitude = ${coord[0]
              }`
            );
            console.log('____________________________________________________');
          });

          // Seperate
          text += '\n\n';
        }
        // Check if the geometry type is LineString
        else if (
          feature.geometry.type === 'Polygon' &&
          isCircle(feature.geometry.coordinates[0], 'polygon').pattern !=
          'circle' && !isCircleMath(feature.geometry.coordinates[0], 'polygon')
        ) {
          // Seperate
          text += 'Polygon ' + (index + 1) + ':\n';

          const coordinates = feature.geometry.coordinates[0];

          // Loop through each coordinate
          coordinates.forEach((coord, index) => {
            text += convertToDMS(coord[1], coord[0]);
            text += ';\n';

            // Console Output:
            /*console.log('DMS: ' + convertToDMS(coord[1], coord[0]));
            console.log(
              `Coordinate ${index + 1}: Latitude = ${coord[1]}, Longitude = ${
                coord[0]
              }`
            );
            console.log('____________________________________________________');*/
          });

          // Seperate
          text += '\n\n';
        }
      });

      return text;
    }
  }

  // Convert LAT/LONG to DMS
  function convertToDMS(lat, lng) {
    function toDMS(degree) {
      const d = Math.floor(degree);
      const minFloat = (degree - d) * 60;
      const m = Math.floor(minFloat);
      const s = Math.floor((minFloat - m) * 60);
      return `${String(d).padStart(2, '0')}:${String(m).padStart(
        2,
        '0'
      )}:${String(s).padStart(2, '0')}`;
    }

    const latDirection = lat >= 0 ? 'N' : 'S';
    const lngDirection = lng >= 0 ? 'E' : 'W';

    const latDMS = `${toDMS(Math.abs(lat))}${latDirection}`;
    const lngDMS = `${toDMS(Math.abs(lng))}${lngDirection}`;

    return `${latDMS} / ${lngDMS}`;
  }

  // Helper function to calculate the distance between two GPS points in nautical miles
  function haversineDistance(coord1, coord2) {
    console.log(coord1, coord2);
    const R = 3440.065; // Radius of the Earth in nautical miles
    const toRadians = (angle) => (Math.PI / 180) * angle;

    const lat1 = toRadians(coord1.lat);
    const lon1 = toRadians(coord1.lon);
    const lat2 = toRadians(coord2.lat);
    const lon2 = toRadians(coord2.lon);

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  function isCircleMath(points, shape) {
    if (points.length < 3) return false; // Need at least 3 points

    // Calculate centroid
    let centroid;

    if (shape == 'circle') {
      centroid = points.reduce(
        (acc, coord) => {
          acc.lat += coord.lat;
          acc.lon += coord.lon;
          return acc;
        },
        { lat: 0, lon: 0 }
      );
    } else {
      centroid = points.reduce(
        (acc, coord) => {
          acc.lat += coord[1];
          acc.lon += coord[0];
          return acc;
        },
        { lat: 0, lon: 0 }
      );
    }

    centroid.lat /= points.length;
    centroid.lon /= points.length;

    let distances;

    // Compute distances
    //const distances = points.map(coord => haversineDistance(centroid, coord));
    // Calculate distances from centroid to each point
    if (shape == 'circle') {
      distances = points.map((coord) =>
        haversineDistance(centroid, coord)
      );
    } else {
      distances = points.map((coord) =>
        haversineDistance(centroid, { lat: coord[1], lon: coord[0] })
      );
    }

    // Check variation in distances
    const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;
    console.log(distances);
    const tolerance = 0.05 * avgDistance; // Allow 5% deviation

    return distances.every(dist => Math.abs(dist - avgDistance) <= tolerance);
  }


  // Function to check if points form a circle using ShapeDetector
  function isCircle(points, shape) {
    var detector = new ShapeDetector(ShapeDetector.defaultShapes);

    // Convert points to the format required by ShapeDetector
    let formattedPoints;

    if (shape == 'circle') {
      formattedPoints = points.map((point) => ({ x: point.lon, y: point.lat }));
    } else {
      formattedPoints = points.map((point) => ({ x: point[0], y: point[1] }));
      console.log(formattedPoints);
    }

    // Identify the shape using ShapeDetector
    const result = detector.spot(formattedPoints);

    console.log(result);

    return result;
  }

  function analyzeCircle(coordinates, featureIndex) {
    let text = ''; // Return text

    if (coordinates.length < 3) {
      throw new Error(
        'At least 3 coordinates are needed to determine a circle.'
      );
    }

    // Calculate centroid (average of latitudes and longitudes)
    const centroid = coordinates.reduce(
      (acc, coord) => {
        acc.lat += coord.lat;
        acc.lon += coord.lon;
        return acc;
      },
      { lat: 0, lon: 0 }
    );

    centroid.lat /= coordinates.length;
    centroid.lon /= coordinates.length;

    // Calculate distances from centroid to each point
    const distances = coordinates.map((coord) =>
      haversineDistance(centroid, coord)
    );

    // Check if distances are approximately equal
    const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;

    text +=
      'Circular Polygon ' +
      (featureIndex + 1) +
      ':\n' +
      convertToDMS(centroid.lat, centroid.lon) +
      ' - ' +
      avgDistance.toFixed(2) +
      'NM\n\n';
    console.log(
      'Circular Polygon ' +
      (featureIndex + 1) +
      ': ' +
      convertToDMS(centroid.lat, centroid.lon) +
      ' - ' +
      avgDistance.toFixed(2) +
      'NM'
    );

    return text;
  }

  render.off = function () {
    context.dispatch.on('change.json', null);
  };

  return render;
};
